import React from 'react';
import { useLanguage } from '../../../../hooks/useLanguage';
import { appData as portfolioData, getPortfolioText } from '../../../../data/portfolioData';
import './Experience.css';

const Experience = () => {
  const { getText, currentLanguage } = useLanguage();

  // Separar experiencia y educación
  const experienceData = (portfolioData.experience || []).map(item => ({ ...item, type: 'trabajo' }));
  const educationData = (portfolioData.education || []).map(item => ({ ...item, type: 'educacion', position: item.degree, company: item.institution }));

  const getTimelineIcon = (type) => {
    const iconMap = {
      'trabajo': '💼',
      'work': '💼',
      'educacion': '🎓',
      'education': '🎓',
      'proyecto': '🚀',
      'project': '🚀',
      'certificacion': '🏆',
      'certification': '🏆'
    };
    return iconMap[type] || '📍';
  };

  const formatDate = (dateString) => {
    const presentText = getText('portfolio.experience.present');
    if (dateString === 'Presente' || dateString === 'Present') return presentText;
    
    const date = new Date(dateString);
    const locale = currentLanguage === 'es' ? 'es-ES' : 'en-US';
    const month = date.toLocaleDateString(locale, { month: 'short' });
    const year = date.getFullYear();
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate === 'Presente' || endDate === 'Present' ? new Date() : new Date(endDate);
    
    const diffTime = Math.abs(end - start);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    
    if (currentLanguage === 'es') {
      if (diffMonths < 12) {
        return `${diffMonths} mes${diffMonths > 1 ? 'es' : ''}`;
      } else {
        const years = Math.floor(diffMonths / 12);
        const months = diffMonths % 12;
        let duration = `${years} año${years > 1 ? 's' : ''}`;
        if (months > 0) {
          duration += ` ${months} mes${months > 1 ? 'es' : ''}`;
        }
        return duration;
      }
    } else {
      if (diffMonths < 12) {
        return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffMonths / 12);
        const months = diffMonths % 12;
        let duration = `${years} year${years > 1 ? 's' : ''}`;
        if (months > 0) {
          duration += ` ${months} month${months > 1 ? 's' : ''}`;
        }
        return duration;
      }
    }
  };

  // Función para renderizar items del timeline
  const renderTimelineItem = (item, index) => (
    <div key={index} className="timeline-item">
      <div className="timeline-marker">
        <div className="timeline-icon">
          {getTimelineIcon(item.type)}
        </div>
        <div className="timeline-line"></div>
      </div>
      
      <div className="timeline-content">
        <div className="timeline-card">
          <div className="card-header">
            <div className="position-info">
              <h3 className="position-title">
                {getPortfolioText(item.position, currentLanguage)}
              </h3>
              <h4 className="company-name">{item.company}</h4>
              {item.location && (
                <span className="location">📍 {getPortfolioText(item.location, currentLanguage)}</span>
              )}
            </div>
            
            <div className="date-info">
              <div className="date-range">
                {item.startDate && item.endDate ? (
                  <>
                    {formatDate(item.startDate)} - {formatDate(item.endDate)}
                  </>
                ) : (
                  getPortfolioText(item.period, currentLanguage) || item.period
                )}
              </div>
              {item.startDate && item.endDate && (
                <div className="duration">
                  {calculateDuration(item.startDate, item.endDate)}
                </div>
              )}
            </div>
          </div>
          
          <div className="card-body">
            <p className="description">
              {getPortfolioText(item.description, currentLanguage)}
            </p>
            
            {item.achievements && item.achievements.length > 0 && (
              <div className="achievements">
                <h5>{getText('portfolio.experience.achievements')}:</h5>
                <ul>
                  {item.achievements.map((achievement, idx) => (
                    <li key={idx}>
                      {getPortfolioText(achievement, currentLanguage)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {item.technologies && item.technologies.length > 0 && (
              <div className="technologies">
                <h5>{getText('portfolio.services.technologies')}:</h5>
                <div className="tech-tags">
                  {item.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>
            )}

            {item.status && (
              <div className="status">
                <span className={`status-badge ${item.type}`}>
                  {getPortfolioText(item.status, currentLanguage)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="experience-section">
      <div className="container">
        {/* Sección de Experiencia Laboral */}
        <div className="section-header">
          <span className="section-tag">{getText('portfolio.experience.title')}</span>
          <h2 className="section-title">{getText('portfolio.experience.title')}</h2>
          <p className="section-description">
            {getText('portfolio.experience.subtitle')}
          </p>
        </div>

        {experienceData.length > 0 && (
          <div className="timeline">
            {experienceData.map((item, index) => renderTimelineItem(item, index))}
          </div>
        )}

        {/* Sección de Educación */}
        <div className="section-header education-header">
          <span className="section-tag">{getText('portfolio.experience.educationTab')}</span>
          <h2 className="section-title">{getText('portfolio.experience.educationTab')}</h2>
          <p className="section-description">
            {currentLanguage === 'es' 
              ? 'Formación académica y certificaciones profesionales'
              : 'Academic background and professional certifications'
            }
          </p>
        </div>

        {educationData.length > 0 && (
          <div className="timeline">
            {educationData.map((item, index) => renderTimelineItem(item, index))}
          </div>
        )}

        <div className="experience-stats">
          <div className="stat-card">
            <div className="stat-number">5+</div>
            <div className="stat-label">{getText('portfolio.about.stats.experience')}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">50+</div>
            <div className="stat-label">{getText('portfolio.about.stats.projects')}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">20+</div>
            <div className="stat-label">{getText('portfolio.about.stats.technologies')}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">100%</div>
            <div className="stat-label">{getText('portfolio.about.stats.clients')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
